import { Component, ElementRef, Inject, LOCALE_ID, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DatePipe, NgTemplateOutlet, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { forkJoin, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime } from 'rxjs/operators';
import { FeatureHelperService } from '../../_helpers/feature-helper/feature-helper.service';
import { hasRole } from '../../_helpers/can-use/current-user-roles';
import { FeatureSwitchService } from '../../_services/feature-switch/feature-switch.service';
import { EmailTemplateService } from '../../_services/email-template/email-template.service';
import { FeatureSwitchKeys } from '../../_constants/feature-switch-keys';
import { AppSettingsService } from '../../_services/app-settings/app-settings.service';
import { MarkupEditorComponent } from '../../shared/markup-editor/markup-editor.component';
import { MarkupEditorOptions } from '../../shared/markup-editor/markup-editor-rules';
import { AdminEmailComponent } from '../../shared/admin-ux/admin-email.component';
import {
  EmailAddressValue, EmailEnvironmentAddresses, EmailPreviewBrand, EmailResolvedAddress, EmailTemplatePreview, EmailTemplateSummary,
  EmailTemplateTestResult, EmailToken, EmailWording, EmailWordingHistory, EmailWordingState
} from '../../_models/admin/email-template.models';

interface TemplateGroup {
  category: string;
  templates: EmailTemplateSummary[];
}

/**
 * Email templates — every email the platform sends, with a preview built from sample data by the same
 * code that builds the real email, and a "send test to me" button.
 *
 * The route needs the EmailTemplatesView feature (canUseGuard); the emailtemplates.catalogue switch decides
 * whether the page is available. Previews never send.
 * A test goes only to the signed-in admin, through the normal email fences, so it can be refused (email
 * switched off) or redirected (EmailOverride); the result says which.
 *
 * The preview is the body as LEAP builds it. When really sent, SendGrid wraps it in the brand's dynamic
 * template, which lives in SendGrid and is not shown here.
 *
 * Wording: each email's subject and body can be replaced from the page. The API checks a draft (known
 * placeholders only, required ones kept) before it saves or previews it. Saved wording is used only while
 * the emailtemplates.overrides switch is on; "Reset to default" deletes it. While editing, the page is the
 * editor beside a live preview of the draft, re-rendered shortly after each change.
 * Every preset address is a settings-store key: the page shows each one's effective value and where it comes
 * from, and someone with the settings editor's permission (ServiceProcessAdmin, the System Controls gate) can
 * edit it inline through the settings admin API, per brand where the key has per-brand values. That source
 * detail, the inline editor and the settings link are behind the emailtemplates.preset-addresses switch: off,
 * each preset address is shown read-only, as its label and value.
 *
 * The body is edited in a plain textarea unless emailtemplates.rich-editor is on; then it is the markup editor
 * (CodeMirror, loaded only then), with each option behind its own sub-switch. Both report every change the
 * same way, so the live preview, placeholder insertion and the API's check work identically in either.
 *
 * While emailtemplates.history-restore is on, each history entry but the newest (the wording now current) has a
 * "Restore this version" button. After a confirm, the API makes that entry's wording current again, checked as a
 * save is, or goes back to the default for a reset entry; the current wording stays in the history.
 */
@Component({
  selector: 'email-templates',
  standalone: true,
  imports: [FormsModule, DatePipe, NgTemplateOutlet, MarkupEditorComponent, AdminEmailComponent],
  templateUrl: 'email-templates.page.html'
})
export class EmailTemplatesPage implements OnInit, OnDestroy {

  /** How long typing must pause before the draft preview re-renders. */
  public static readonly draftPreviewDelayMs = 500;

  public available: boolean | null = null;
  public loading: boolean = false;
  public error: string | null = null;

  public templates: EmailTemplateSummary[] = [];
  public brands: EmailPreviewBrand[] = [];
  public environmentAddresses: EmailEnvironmentAddresses | null = null;

  /** Whether the preset addresses show their source and can be edited here (emailtemplates.preset-addresses). */
  public presetSettings: boolean = false;
  /** Whether this user may edit preset addresses: the settings editor's own gate, and the switch above. */
  public canEdit: boolean = false;
  /** The address being edited: its setting key, or key|applicationId for one brand's value. */
  public editing: string | null = null;
  public addressDraft: string = '';
  public savingAddress: boolean = false;
  public addressError: string | null = null;
  public groups: TemplateGroup[] = [];
  public search: string = '';

  public selected: EmailTemplateSummary | null = null;
  public brandId: number | null = null;
  public preview: EmailTemplatePreview | null = null;
  public previewHtml: SafeHtml | null = null;
  public previewLoading: boolean = false;

  public sending: boolean = false;
  public testResult: EmailTemplateTestResult | null = null;

  public wording: EmailWordingState | null = null;
  /** The wording being edited; null when the editor is closed. */
  public draft: EmailWording | null = null;
  public draftErrors: string[] = [];
  /** True while the preview shows the draft rather than the wording really used. */
  public previewIsDraft: boolean = false;
  public savingWording: boolean = false;
  public wordingMessage: string | null = null;
  /** Whether history entries can be restored (emailtemplates.history-restore). */
  public historyRestore: boolean = false;
  /** Why the last restore was refused: the entry no longer passes the save's checks. */
  public restoreErrors: string[] = [];
  /** The wording the editor opened on, to tell whether the draft has changed. */
  private draftOrigin: EmailWording | null = null;
  private readonly draftEdits = new Subject<void>();
  private draftEditsSubscription: Subscription;
  private draftPreviewRequest = 0;

  /** The markup editor's options (from its sub-switches), or null for the plain textarea. */
  public richEditor: MarkupEditorOptions | null = null;
  @ViewChild('bodyInput') private bodyInput?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('bodyEditor') private bodyEditor?: MarkupEditorComponent;

  constructor(
    public router: Router,
    private featureHelperService: FeatureHelperService,
    private featureSwitches: FeatureSwitchService,
    private service: EmailTemplateService,
    private sanitizer: DomSanitizer,
    private settings: AppSettingsService,
    @Inject(LOCALE_ID) private locale: string
  ) {
    this.draftEditsSubscription = this.draftEdits.pipe(debounceTime(EmailTemplatesPage.draftPreviewDelayMs))
      .subscribe(() => this.previewDraft());
  }

  ngOnDestroy(): void {
    this.draftEditsSubscription.unsubscribe();
  }

  /** One line on which wording this email sends. */
  public get wordingStatus(): string {
    const w = this.wording;
    if (!w) {
      return '';
    }
    if (!w.saved) {
      return 'Default wording';
    }
    if (!w.overridesEnabled) {
      return 'Edited wording saved, not in use: email wording overrides are off, so the default is sent';
    }
    return w.savedErrors.length ? 'Edited wording no longer fits: this email is not sent' : 'Edited wording';
  }

  public get draftChanged(): boolean {
    return !!this.draft && !!this.draftOrigin
      && (this.draft.subject !== this.draftOrigin.subject || this.draft.body !== this.draftOrigin.body);
  }

  /** Called on every keystroke in the editor: re-renders the preview once typing pauses. */
  public draftEdited(): void {
    this.draftEdits.next();
  }

  ngOnInit(): void {
    // canUseGuard has already required EmailTemplatesView on the route.
    // The settings editor is System Controls': the grant AND the ADMIN role.
    const settingsPermission = hasRole('ADMIN') && !!this.featureHelperService.checkFeaturePermissions(['ServiceProcessAdmin'])
      .find(f => f.feature === 'ServiceProcessAdmin')?.enabled;

    this.featureSwitches.isEnabled(FeatureSwitchKeys.emailTemplatesPresetAddresses).subscribe(on => {
      this.presetSettings = on;
      this.canEdit = on && settingsPermission;
    });
    this.loadRichEditorSwitches();
    this.featureSwitches.isEnabled(FeatureSwitchKeys.emailTemplatesHistoryRestore).pipe(catchError(() => of(false)))
      .subscribe(on => this.historyRestore = on);
    this.featureSwitches.isEnabled(FeatureSwitchKeys.emailTemplatesCatalogue).subscribe(on => {
      this.available = on;
      if (on) {
        this.load();
      }
    });
  }

  /**
   * The markup editor and its options. The API lists a sub-switch as on only while the parent is, so each
   * option is read as it comes; with the parent off the page keeps the plain textarea.
   */
  private loadRichEditorSwitches(): void {
    const k = FeatureSwitchKeys;
    const on = (key: string) => this.featureSwitches.isEnabled(key).pipe(catchError(() => of(false)));
    forkJoin({
      enabled: on(k.emailTemplatesRichEditor),
      highlight: on(k.emailTemplatesRichEditorHighlight),
      placeholders: on(k.emailTemplatesRichEditorPlaceholders),
      autocomplete: on(k.emailTemplatesRichEditorAutocomplete),
      lint: on(k.emailTemplatesRichEditorLint),
      lineNumbers: on(k.emailTemplatesRichEditorLineNumbers),
      autoclose: on(k.emailTemplatesRichEditorAutoclose),
      format: on(k.emailTemplatesRichEditorFormat),
      search: on(k.emailTemplatesRichEditorSearch),
      wrap: on(k.emailTemplatesRichEditorWrap)
    }).subscribe(({ enabled, ...options }) => this.richEditor = enabled ? options : null);
  }

  private load(): void {
    this.loading = true;
    this.service.getCatalogue().pipe(catchError(() => of(null))).subscribe(catalogue => {
      this.loading = false;
      if (!catalogue) {
        this.error = 'The email templates could not be loaded.';
        return;
      }
      this.templates = catalogue.templates ?? [];
      this.brands = catalogue.brands ?? [];
      this.environmentAddresses = catalogue.environment ?? null;
      if (this.brandId === null || !this.brands.some(b => b.applicationId === this.brandId)) {
        this.brandId = this.brands.length ? this.brands[0].applicationId : null;
      }
      this.applySearch();
      // After a save the catalogue is re-read: keep the email that was open.
      const keep = this.selected ? this.templates.find(t => t.key === this.selected.key) : null;
      if (keep) {
        this.selected = keep;
      } else if (this.templates.length) {
        this.select(this.templates[0]);
      }
    });
  }

  public applySearch(): void {
    const term = this.search.trim().toLowerCase();
    const matches = this.templates.filter(t => !term
      || [t.name, t.key, t.trigger, t.recipients, t.processKey].some(v => (v ?? '').toLowerCase().includes(term)));
    const byCategory = new Map<string, EmailTemplateSummary[]>();
    matches.forEach(t => byCategory.set(t.category, [...(byCategory.get(t.category) ?? []), t]));
    this.groups = [...byCategory.entries()].map(([category, templates]) => ({ category, templates }));
  }

  public select(template: EmailTemplateSummary): void {
    this.selected = template;
    this.testResult = null;
    this.closeEditor();
    this.wordingMessage = null;
    this.restoreErrors = [];
    this.loadPreview();
    this.loadWording();
  }

  public brandChanged(): void {
    this.testResult = null;
    if (this.draft) {
      this.previewDraft();
    } else {
      this.loadPreview();
    }
  }

  private loadWording(): void {
    this.wording = null;
    if (!this.selected?.editable) {
      return;
    }
    const key = this.selected.key;
    this.service.getWording(key).pipe(catchError(() => of(null))).subscribe(state => {
      if (this.selected?.key === key) {
        this.wording = state;
      }
    });
  }

  /** Opens the editor on the wording really used: the saved one if any, else the default. */
  public edit(): void {
    const source = this.wording?.saved ?? this.wording?.default;
    if (!source) {
      return;
    }
    this.draftOrigin = { subject: source.subject, body: source.body };
    this.draft = { ...this.draftOrigin };
    this.draftErrors = [];
    this.wordingMessage = null;
    this.previewDraft();
  }

  /** Starts the draft again from the code wording, without saving anything. */
  public startFromDefault(): void {
    if (this.wording?.default) {
      this.draft = { subject: this.wording.default.subject, body: this.wording.default.body };
      this.draftErrors = [];
      this.previewDraft();
    }
  }

  /** Leaves the editor; asks first when the draft has unsaved changes. */
  public cancelEditing(): void {
    if (this.draftChanged && !window.confirm('Discard your unsaved changes to this wording?')) {
      return;
    }
    this.closeEditor();
  }

  public closeEditor(): void {
    const wasDraft = this.previewIsDraft;
    this.draftOrigin = null;
    this.draft = null;
    this.draftErrors = [];
    this.previewIsDraft = false;
    if (wasDraft) {
      this.loadPreview();
    }
  }

  /** A token as it is written in a wording: {{Name}}. */
  public placeholder(token: EmailToken): string {
    return `{{${token.name}}}`;
  }

  /** The placeholder buttons: adds {{Name}} at the cursor of whichever body editor is showing. */
  public insertTokenAtCursor(token: EmailToken): void {
    const target = this.richEditor ? this.bodyEditor : this.bodyInput?.nativeElement;
    if (target) {
      this.insertToken(token, target);
    }
  }

  /** Adds {{Name}} at the cursor in the body (or at the end): the textarea, or the markup editor. */
  public insertToken(token: EmailToken, body: HTMLTextAreaElement | MarkupEditorComponent): void {
    if (!this.draft) {
      return;
    }
    const placeholder = this.placeholder(token);
    if (body instanceof MarkupEditorComponent) {
      // The editor types it over the selection and reports the edit through ngModel; keep the draft in step
      // here too, so the preview is right even before that round trip.
      body.insertAtCursor(placeholder);
      this.draft.body = body.value;
      this.draftEdited();
      return;
    }
    const start = body.selectionStart ?? this.draft.body.length;
    const end = body.selectionEnd ?? start;
    this.draft.body = this.draft.body.slice(0, start) + placeholder + this.draft.body.slice(end);
    this.draftEdited();
    setTimeout(() => {
      body.focus();
      body.setSelectionRange(start + placeholder.length, start + placeholder.length);
    });
  }

  public previewDraft(): void {
    if (!this.selected || !this.draft) {
      return;
    }
    this.previewLoading = true;
    // Only the latest request may update the page: typing fires several, and they can return out of order.
    const request = ++this.draftPreviewRequest;
    this.service.previewDraft(this.selected.key, this.brandId, { ...this.draft }).pipe(catchError(() => of(null))).subscribe(result => {
      if (request !== this.draftPreviewRequest || !this.draft) {
        return;
      }
      this.previewLoading = false;
      if (!result) {
        this.draftErrors = ['The draft could not be previewed.'];
        return;
      }
      this.draftErrors = result.errors ?? [];
      if (result.preview) {
        this.previewIsDraft = true;
        this.showPreview(result.preview);
      }
    });
  }

  public saveWording(): void {
    if (!this.selected || !this.draft || this.savingWording) {
      return;
    }
    this.savingWording = true;
    this.service.saveWording(this.selected.key, this.draft).pipe(catchError(() => of(null))).subscribe(outcome => {
      this.savingWording = false;
      if (!outcome) {
        this.draftErrors = ['The wording could not be saved.'];
        return;
      }
      if (outcome.errors?.length) {
        this.draftErrors = outcome.errors;
        return;
      }
      this.applySaved(outcome.state, 'Wording saved.');
    });
  }

  public resetWording(): void {
    if (!this.selected || !this.wording?.saved || this.savingWording) {
      return;
    }
    if (!window.confirm(`Delete the saved wording for "${this.selected.name}" and go back to the default?`)) {
      return;
    }
    this.savingWording = true;
    this.service.resetWording(this.selected.key).pipe(catchError(() => of(null))).subscribe(state => {
      this.savingWording = false;
      if (!state) {
        this.wordingMessage = 'The wording could not be reset.';
        return;
      }
      this.applySaved(state, 'Back to the default wording.');
    });
  }

  /** How a history entry reads: saved, reset to default, or restored from the entry it brought back. */
  public historyLabel(h: EmailWordingHistory): string {
    if (h.action === 'Reset') {
      return 'reset to default';
    }
    if (h.action === 'Restore') {
      const from = h.restoredFromUtc
        ?? this.wording?.history.find(e => e.emailTemplateOverrideHistoryId === h.restoredFromId)?.changedUtc;
      return from ? `restored from ${this.formatWhen(from)}` : 'restored';
    }
    return 'saved';
  }

  /** Asks first, then makes a history entry's wording current again; the current wording stays in the history. */
  public restoreVersion(h: EmailWordingHistory): void {
    if (!this.historyRestore || !this.selected || this.savingWording) {
      return;
    }
    const what = h.subject == null ? 'the default wording' : `"${h.subject}"`;
    if (!window.confirm(`Restore this version of "${this.selected.name}"?\n\n`
      + `${this.formatWhen(h.changedUtc)}, by ${h.changedBy || 'unknown'}: ${what}\n\n`
      + 'The current wording is kept in the history.')) {
      return;
    }
    this.savingWording = true;
    this.restoreErrors = [];
    this.wordingMessage = null;
    this.service.restoreWording(this.selected.key, h.emailTemplateOverrideHistoryId).pipe(catchError(() => of(null))).subscribe(outcome => {
      this.savingWording = false;
      if (!outcome) {
        this.wordingMessage = 'The version could not be restored.';
        return;
      }
      if (outcome.errors?.length) {
        this.restoreErrors = outcome.errors;
        return;
      }
      this.applySaved(outcome.state, h.subject == null ? 'Back to the default wording.' : 'Version restored.');
    });
  }

  private formatWhen(utc: string): string {
    return formatDate(utc, 'd MMM yyyy HH:mm', this.locale);
  }

  private applySaved(state: EmailWordingState | null, message: string): void {
    this.wording = state;
    if (this.selected) {
      this.selected.hasSavedWording = !!state?.saved;
    }
    this.wordingMessage = message;
    this.restoreErrors = [];
    this.draft = null;
    this.draftErrors = [];
    this.previewIsDraft = false;
    this.loadPreview();
  }

  private loadPreview(): void {
    if (!this.selected) {
      return;
    }
    this.previewLoading = true;
    this.service.preview(this.selected.key, this.brandId).pipe(catchError(() => of(null))).subscribe(preview => {
      this.previewLoading = false;
      this.showPreview(preview);
    });
  }

  private showPreview(preview: EmailTemplatePreview | null): void {
    this.preview = preview;
    // Rendered in a sandboxed iframe with no scripts (sandbox=""), from sample data and admin-written wording.
    this.previewHtml = preview?.isHtml ? this.sanitizer.bypassSecurityTrustHtml(preview.body) : null;
  }

  public sendTest(): void {
    if (!this.selected || this.sending) {
      return;
    }
    this.sending = true;
    this.testResult = null;
    this.service.sendTest(this.selected.key, this.brandId).pipe(catchError(() => of(null))).subscribe(result => {
      this.sending = false;
      this.testResult = result ?? { sent: false, addressedTo: null, redirectedTo: null, reason: 'The test could not be sent.' };
    });
  }

  // ── Preset addresses ───────────────────────────────────────────────────────────────────────────

  /** The edit key of an address: the setting key, or key|applicationId for one brand's own value. */
  public editKey(preset: EmailResolvedAddress, value?: EmailAddressValue | null): string {
    return value?.applicationId != null ? preset.settingKey + '|' + value.applicationId : preset.settingKey;
  }

  /** Whether one brand's value can be edited on its own: only where the setting already has per-brand values. */
  public canEditBrand(preset: EmailResolvedAddress, value: EmailAddressValue): boolean {
    return this.canEdit && !!preset.settingKey && preset.hasBrandOverrides && value.applicationId != null;
  }

  /** What to show for an address: the address, "Not used", or null when there is none. */
  public shown(address: string | null, notUsed: boolean | undefined): string | null {
    return notUsed ? 'Not used' : address;
  }

  public blankHint(preset: EmailResolvedAddress): string {
    return preset.blankMeans === 'off'
      ? 'Blank: no address (off).'
      : 'Blank: use this environment\'s configured address.' + (preset.notUsedValue ? ' "Not used" switches it off.' : '');
  }

  /** Opens the inline editor on the stored value (the global one, or the brand's own); "Not used" opens blank. */
  public startEdit(preset: EmailResolvedAddress, value?: EmailAddressValue | null): void {
    if (!this.canEdit || !preset.settingKey) {
      return;
    }
    const stored = value?.applicationId != null ? value.storedValue : preset.storedValue;
    this.editing = this.editKey(preset, value);
    this.addressDraft = stored && stored !== preset.notUsedValue ? stored : '';
    this.addressError = null;
  }

  public cancelEdit(): void {
    this.editing = null;
    this.addressDraft = '';
    this.addressError = null;
  }

  public saveDraft(preset: EmailResolvedAddress, value?: EmailAddressValue | null): void {
    this.write(preset, value, (this.addressDraft ?? '').trim());
  }

  /** Stores the "Not used" value, for an address whose blank means this environment's config. */
  public saveNotUsed(preset: EmailResolvedAddress, value?: EmailAddressValue | null): void {
    if (preset.notUsedValue) {
      this.write(preset, value, preset.notUsedValue);
    }
  }

  public openSettings(event?: Event): void {
    event?.preventDefault();
    this.router.navigate(['/service-processes'], { queryParams: { tab: 'settings' } });
  }

  /** Saves through the settings admin API (the endpoints System Controls -> Settings uses), then re-reads the catalogue. */
  private write(preset: EmailResolvedAddress, value: EmailAddressValue | null | undefined, stored: string): void {
    if (!this.canEdit || !preset.settingKey || this.savingAddress) {
      return;
    }
    const call: Observable<unknown> = value?.applicationId != null
      ? this.settings.saveBrandOverride(preset.settingKey, value.applicationId, stored)
      : this.settings.saveSetting(preset.settingKey, stored);
    this.savingAddress = true;
    this.addressError = null;
    call.subscribe({
      next: () => {
        this.savingAddress = false;
        this.editing = null;
        this.addressDraft = '';
        this.load();
      },
      error: error => {
        this.savingAddress = false;
        this.addressError = 'Could not save "' + preset.label + '": ' + ((error && error.message) || 'please try again.');
      }
    });
  }
}
